/**
 * SheetSurfaceContext
 *
 * Answers one question for any subtree: "am I rendering inside a sheet/modal surface?"
 *
 * True inside a `SheetService` surface (bottom sheet **or** dialog), false on a page.
 * Provided by `BottomSheetView` / `DialogView` around `state.content` only — the provider
 * deliberately does NOT wrap the shell chrome (drag handle, title, CTA row) or anything
 * above the sheet, so a page tree never resolves `true`. Any future modal/overlay surface
 * that renders content outside those two views (e.g. a new host under `components/common/`)
 * must add the provider itself, or content inside it will be treated as page content.
 *
 * First consumer: `useGridColumnsCount` (`presentation/hooks/useGridColumnsCount.ts`), which
 * returns the fixed `CONTENT_CONSTANTS.gridLayout.columnsCount` inside a sheet instead of the
 * responsive portrait/landscape count — so every sheet grid is correct by construction rather
 * than by author discipline.
 */

import React, { createContext, useContext } from 'react';

/**
 * Module-private: the context object is intentionally NOT exported. Consumers read it through
 * `useIsInSheetSurface` and producers wrap with `SheetSurfaceProvider`, which keeps the set of
 * surfaces that can claim "I am a sheet" closed and greppable.
 */
const SheetSurfaceContext = createContext<boolean>(false);

/**
 * Marks its subtree as rendering inside a sheet/modal surface.
 *
 * The value is the literal `true` — referentially stable, so no memoization is needed and the
 * provider adds no re-render surface. Renders no DOM node, so wrapping a content slot leaves
 * the existing DOM shape (scroll containers, refs, `data-qa-*` handles) untouched.
 */
export const SheetSurfaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SheetSurfaceContext.Provider value={true}>{children}</SheetSurfaceContext.Provider>
);

/**
 * `true` when the caller renders inside a sheet/modal surface, `false` on a page.
 *
 * Deliberately defaults to `false` with no throw (unlike `useExpauseSheetContext`), because
 * every page-level caller legitimately renders outside any provider.
 */
export const useIsInSheetSurface = (): boolean => useContext(SheetSurfaceContext);
